/**
 * Security Utilities
 *
 * Comprehensive security utilities for:
 * - Input sanitization (XSS prevention)
 * - Secure storage management
 * - Token management with expiration
 * - CSRF protection helpers
 */

import DOMPurify from 'dompurify';

// ============================================
// Input Sanitization (XSS Prevention)
// ============================================

/**
 * Sanitize HTML content to prevent XSS attacks
 * Uses DOMPurify library for comprehensive sanitization
 *
 * @param {string} dirty - Potentially unsafe HTML string
 * @param {object} config - DOMPurify configuration options
 * @returns {string} Sanitized HTML string safe for rendering
 */
export const sanitizeHTML = (dirty, config = {}) => {
  if (typeof dirty !== 'string') {
    console.warn('sanitizeHTML: Input is not a string, returning empty string');
    return '';
  }

  const defaultConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'title', 'class', 'id'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
    ...config
  };

  return DOMPurify.sanitize(dirty, defaultConfig);
};

/**
 * Sanitize user input for plain text (strip all HTML)
 *
 * @param {string} input - User input string
 * @returns {string} Sanitized plain text
 */
export const sanitizeText = (input) => {
  if (typeof input !== 'string') return '';

  // Remove all HTML tags
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true
  }).trim();
};

/**
 * Sanitize SQL-like inputs (prevent SQL injection patterns)
 * Escapes special characters that could be used in SQL injection
 *
 * @param {string} input - User input string
 * @returns {string} Sanitized string
 */
export const sanitizeSQLInput = (input) => {
  if (typeof input !== 'string') return '';

  // Escape SQL special characters
  return input
    .replace(/'/g, "''")  // Escape single quotes
    .replace(/;/g, '')     // Remove semicolons
    .replace(/--/g, '')    // Remove SQL comments
    .replace(/\/\*/g, '')  // Remove multi-line comment start
    .replace(/\*\//g, ''); // Remove multi-line comment end
};

/**
 * Escape HTML entities to prevent XSS
 * Alternative to DOMPurify for simple cases
 *
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export const escapeHTML = (str) => {
  if (typeof str !== 'string') return '';

  const htmlEntities = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;'
  };

  return str.replace(/[&<>"'/]/g, (char) => htmlEntities[char]);
};

/**
 * Validate and sanitize URL to prevent javascript: and data: schemes
 *
 * @param {string} url - URL to validate
 * @returns {string|null} Sanitized URL or null if invalid
 */
export const sanitizeURL = (url) => {
  if (typeof url !== 'string') return null;

  const trimmedURL = url.trim();

  // Block dangerous protocols
  const dangerousProtocols = /^(javascript|data|vbscript|file):/i;
  if (dangerousProtocols.test(trimmedURL)) {
    console.warn('sanitizeURL: Dangerous protocol detected, blocking URL');
    return null;
  }

  // Allow http, https, mailto, and relative URLs
  const allowedProtocols = /^(https?|mailto):/i;
  const isRelative = trimmedURL.startsWith('/') || trimmedURL.startsWith('#');

  if (allowedProtocols.test(trimmedURL) || isRelative) {
    return trimmedURL;
  }

  // If no protocol, assume https for external links
  if (!trimmedURL.includes(':') && !isRelative) {
    return `https://${trimmedURL}`;
  }

  console.warn('sanitizeURL: Invalid URL format, blocking');
  return null;
};

/**
 * Sanitize file name to prevent directory traversal
 *
 * @param {string} filename - File name to sanitize
 * @returns {string} Sanitized file name
 */
export const sanitizeFileName = (filename) => {
  if (typeof filename !== 'string') return '';

  return filename
    .replace(/\.\./g, '')  // Remove parent directory references
    .replace(/[/\\]/g, '') // Remove path separators
    .replace(/[<>:"|?*]/g, '') // Remove invalid filename characters
    .trim();
};

// ============================================
// Secure Storage Management
// ============================================

/**
 * Secure Storage Wrapper
 * Provides encrypted storage with automatic expiration
 *
 * Note: This uses localStorage with Base64 encoding. For production,
 * consider using httpOnly cookies for sensitive tokens.
 */
class SecureStorage {
  constructor() {
    this.storage = localStorage;
    this.prefix = 'secure_';
  }

  /**
   * Simple XOR encryption (NOT cryptographically secure)
   * For demo purposes only. In production, use Web Crypto API
   * or store sensitive data in httpOnly cookies
   */
  _encrypt(data, key = 'rubbert_2025') {
    let result = '';
    for (let i = 0; i < data.length; i++) {
      result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(result); // Base64 encode
  }

  _decrypt(data, key = 'rubbert_2025') {
    try {
      const decoded = atob(data); // Base64 decode
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return result;
    } catch (e) {
      console.error('Decryption failed:', e);
      return null;
    }
  }

  /**
   * Set item with optional expiration
   *
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @param {number} expiresIn - Expiration time in milliseconds (optional)
   */
  setItem(key, value, expiresIn = null) {
    const item = {
      value: value,
      timestamp: Date.now(),
      expiresAt: expiresIn ? Date.now() + expiresIn : null
    };

    const encrypted = this._encrypt(JSON.stringify(item));
    this.storage.setItem(this.prefix + key, encrypted);
  }

  /**
   * Get item from storage
   * Automatically removes expired items
   *
   * @param {string} key - Storage key
   * @returns {any} Stored value or null if not found/expired
   */
  getItem(key) {
    const encrypted = this.storage.getItem(this.prefix + key);
    if (!encrypted) return null;

    const decrypted = this._decrypt(encrypted);
    if (!decrypted) return null;

    try {
      const item = JSON.parse(decrypted);

      // Check expiration
      if (item.expiresAt && Date.now() > item.expiresAt) {
        this.removeItem(key);
        return null;
      }

      return item.value;
    } catch (e) {
      console.error('Failed to parse stored item:', e);
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Remove item from storage
   */
  removeItem(key) {
    this.storage.removeItem(this.prefix + key);
  }

  /**
   * Clear all secure storage items
   */
  clear() {
    Object.keys(this.storage).forEach(key => {
      if (key.startsWith(this.prefix)) {
        this.storage.removeItem(key);
      }
    });
  }

  /**
   * Check if item exists and is not expired
   */
  hasItem(key) {
    return this.getItem(key) !== null;
  }
}

export const secureStorage = new SecureStorage();

// ============================================
// Token Management
// ============================================

const TOKEN_KEY = 'auth_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_LIFETIME = 60 * 60 * 1000; // 60 minutes (matching Laravel backend)

/**
 * Set authentication token with expiration
 *
 * @param {string} token - JWT token
 * @param {number} expiresIn - Token lifetime in milliseconds
 */
export const setAuthToken = (token, expiresIn = TOKEN_LIFETIME) => {
  secureStorage.setItem(TOKEN_KEY, token, expiresIn);
  secureStorage.setItem(TOKEN_EXPIRY_KEY, Date.now() + expiresIn);
};

/**
 * Get authentication token if not expired
 *
 * @returns {string|null} Token or null if expired/not found
 */
export const getAuthToken = () => {
  return secureStorage.getItem(TOKEN_KEY);
};

/**
 * Remove authentication token
 */
export const removeAuthToken = () => {
  secureStorage.removeItem(TOKEN_KEY);
  secureStorage.removeItem(TOKEN_EXPIRY_KEY);
  secureStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Check if token is expired or will expire soon
 *
 * @param {number} bufferMinutes - Minutes before expiry to consider expired
 * @returns {boolean} True if token is expired or will expire soon
 */
export const isTokenExpired = (bufferMinutes = 5) => {
  const expiry = secureStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return true;

  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() + bufferMs >= expiry;
};

/**
 * Get time until token expiration in milliseconds
 *
 * @returns {number} Milliseconds until expiration (0 if expired)
 */
export const getTokenTimeRemaining = () => {
  const expiry = secureStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return 0;

  const remaining = expiry - Date.now();
  return Math.max(0, remaining);
};

/**
 * Set refresh token
 */
export const setRefreshToken = (token) => {
  secureStorage.setItem(REFRESH_TOKEN_KEY, token);
};

/**
 * Get refresh token
 */
export const getRefreshToken = () => {
  return secureStorage.getItem(REFRESH_TOKEN_KEY);
};

// ============================================
// Form Validation Helpers
// ============================================

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (basic)
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[\d\s\-+()]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
};

/**
 * Validate password strength
 * Returns object with isValid and requirements
 */
export const validatePassword = (password) => {
  const requirements = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  const isValid = Object.values(requirements).every(req => req);

  return { isValid, requirements };
};

/**
 * Validate number range
 */
export const isNumberInRange = (value, min = -Infinity, max = Infinity) => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Validate date format and range
 */
export const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

// ============================================
// Rate Limiting Helpers
// ============================================

/**
 * Parse retry-after header from rate limit response
 *
 * @param {object} error - Axios error object
 * @returns {number|null} Seconds until retry allowed
 */
export const getRetryAfter = (error) => {
  if (error.response?.status !== 429) return null;

  const retryAfter = error.response.headers['retry-after'] ||
                     error.response.data?.retry_after;

  return retryAfter ? parseInt(retryAfter, 10) : 60; // Default to 60 seconds
};

/**
 * Format rate limit error message
 */
export const formatRateLimitError = (error) => {
  const retryAfter = getRetryAfter(error);
  if (!retryAfter) return 'Too many requests. Please try again later.';

  if (retryAfter < 60) {
    return `Too many requests. Please wait ${retryAfter} seconds and try again.`;
  }

  const minutes = Math.ceil(retryAfter / 60);
  return `Too many requests. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} and try again.`;
};

// ============================================
// Export All
// ============================================

const securityUtils = {
  // Sanitization
  sanitizeHTML,
  sanitizeText,
  sanitizeSQLInput,
  escapeHTML,
  sanitizeURL,
  sanitizeFileName,

  // Secure Storage
  secureStorage,

  // Token Management
  setAuthToken,
  getAuthToken,
  removeAuthToken,
  isTokenExpired,
  getTokenTimeRemaining,
  setRefreshToken,
  getRefreshToken,

  // Validation
  isValidEmail,
  isValidPhone,
  validatePassword,
  isNumberInRange,
  isValidDate,

  // Rate Limiting
  getRetryAfter,
  formatRateLimitError
};
export default securityUtils;
